import React, { useMemo, useCallback, useEffect, useState, useRef } from 'react';
import { PlusCircleIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Controller, FormProvider, useFieldArray, useForm } from 'react-hook-form';
import TGButton from 'src/components/ui/TGButton';
import { FlexRow } from 'src/utils/flexLayouts';
import Select from 'src/components/ui/form/select';
import { Inconsistences } from './Inconsistences';
import { FormValues } from '../../types/typesVPO';

import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import { selectModelData } from '../../store/filtersData/selector';
import { addDataVPO, deleteDataVPO, setLastAddedId, fetchInconsistenciesByActionIdRequest, clearInconsistenciesByAction } from '../../store/formAddActions/actions';
import { fetchData } from '../../store/filtersData/actions';
import { selectLastAddedId, selectLastDocId, selectInconsistenciesByAction } from '../../store/formAddActions/selectors';
import { months } from 'moment';
import { selectCurrentUser } from 'src/modules/users/selector';
import Loading from 'src/components/Loading';

interface CreateDoingFormProps {
  toggleDoing: (value: boolean) => void;
  isInconsistencyRequired?: boolean;
  isNewForm?: boolean;
  isLoading?: boolean;
  resetSelect?: boolean;
  context?: any;
  onActionAdded?: () => void;
  predefinedValues?: {
    group: any;
    factory: any;
    column: any;
    subdivision: any;
    columnBlock: any;
    demand: any;
    process: any;
  };
  firstActionId?: number;
  editMode?: boolean;
  actionData?: any;
  loader?: boolean; 
}

export const CreateDoingForm = React.memo(
  ({ toggleDoing, isInconsistencyRequired = true, predefinedValues, isNewForm, isLoading, resetSelect, context, firstActionId, onActionAdded, editMode = false, actionData, loader = false }: CreateDoingFormProps) => {
    // console.log('predefinedValues', predefinedValues);

    const dispatch = useAppDispatch();

    const ColumnVPO = useAppSelector((state: any) => selectModelData(state, 'vpo-columns'));
    const GroupVPO = useAppSelector((state: any) => selectModelData(state, 'vpo-groups').filter((group) => group.title !== 'SOP'));
    const FactoryVPO = useAppSelector((state: any) => selectModelData(state, 'vpo-factories'));
    const DepartmentVPO = useAppSelector((state: any) => selectModelData(state, 'vpo-departments'));
    const ColumnBlockVPO = useAppSelector((state: any) => selectModelData(state, 'vpo-column-blocks'));
    const RequirementVPO = useAppSelector((state: any) => selectModelData(state, 'vpo-requirements'));

    const lastAddedId = useAppSelector((state) => selectLastAddedId(state, 'vpo-actions'));
    const lastAddedIdforDelete = useAppSelector((state) => selectLastDocId(state, 'vpo-actions'));
    const loadedInconsistencies = useAppSelector(selectInconsistenciesByAction);
    // console.log('lastAddedId',lastAddedId)
    const user = useAppSelector(selectCurrentUser);

    const { currentMonth, currentYear } = context;

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isInconsistenciesLoaded, setIsInconsistenciesLoaded] = useState(false);

    const debounceGroupRef = useRef<NodeJS.Timeout>();
    const debounceFactoryColumnRef = useRef<NodeJS.Timeout>();
    const debounceColumnRef = useRef<NodeJS.Timeout>();
    const debounceColumnBlockRef = useRef<NodeJS.Timeout>();
    const prevFirstActionIdRef = useRef<number | undefined>();

    const defaultValues = useMemo(
      () => ({
        column: null,
        columnBlock: null,
        subdivision: null,
        process: null,
        demand: null,
        group: null,
        factory: null,
        inconsistencies: [{ inconsistency: '' }],
      }),
      []
    );

    const methods = useForm<FormValues>({ defaultValues });
    const { handleSubmit, control, watch, setValue, getValues, reset, formState } = methods;
    const { fields, append, remove, replace } = useFieldArray({ control, name: 'inconsistencies' });

    const selectedColumn = watch('column');
    const selectedGroup = watch('group');
    const selectedFactory = watch('factory');
    const selectedColumnBlock = watch('columnBlock');
    const selectedDemand = watch('demand');
    const selectedProcess = watch('process');

    const isGroupFromPredefined = !!(predefinedValues?.group && selectedGroup?.id === predefinedValues.group.id);
    const isFactoryFromPredefined = !!(predefinedValues?.factory && selectedFactory?.id === predefinedValues.factory.id);
    const isColumnFromPredefined = !!(predefinedValues?.column && selectedColumn?.id === predefinedValues.column.id);
    const isColumnBlockFromPredefined = !!(predefinedValues?.columnBlock && selectedColumnBlock?.id === predefinedValues.columnBlock.id);

    const isFactoryAccessible = useCallback(
      (factoryId: any) => {
        if (user.infoVPO.zone || user.infoVPO.adminRegion) return true;
        if (user.infoVPO.admin) {
          return user.infoVPO.factory.findIndex((item: any) => item.factoryID === factoryId) >= 0;
        }
        if (user.infoVPO.user) {
          const hasFactoryAccess = user.infoVPO.factory.findIndex((item: any) => item.factoryID === factoryId) >= 0;
          const hasGroupAccess = selectedGroup ? user.infoVPO.directoryTitle.indexOf(selectedGroup.name) >= 0 : false;
          return hasFactoryAccess && hasGroupAccess;
        }

        return false;
      },
      [user, selectedGroup]
    );

    const factoryOptions = useMemo(() => {
      if (!FactoryVPO?.length) return [];
      return FactoryVPO
        .filter((item: any) => isFactoryAccessible(item.id))
        .map((item: any) => ({
          id: item.id,
          name: item.title,
        }));
    }, [FactoryVPO, isFactoryAccessible]);

    useEffect(() => {
      if (prevFirstActionIdRef.current !== firstActionId) {
        setIsInconsistenciesLoaded(false);
        if (prevFirstActionIdRef.current !== undefined) {
          replace([{ inconsistency: '' }]);
        }
      }
      prevFirstActionIdRef.current = firstActionId;
    }, [firstActionId, replace]);

    useEffect(() => {
      if (!firstActionId) {
        setIsInconsistenciesLoaded(false);
        return;
      }

      const timer = setTimeout(() => {
        dispatch(fetchInconsistenciesByActionIdRequest(firstActionId));
      }, 500); 

      return () => clearTimeout(timer);
    }, [firstActionId, dispatch]);

    const currentActionInconsistencies = useMemo(() => {
      if (!firstActionId || !loadedInconsistencies) return [];
      return loadedInconsistencies.filter((inc: any) => inc.IdVPOactions === firstActionId);
    }, [loadedInconsistencies, firstActionId]);

    //! Получаю группы
    useEffect(() => {
      dispatch(
        fetchData(
          'vpo-groups',
          {},
          {
            page: 1,
            pageSize: 100,
          }
        )
      );
    }, [dispatch]);

    //! При изменении группы получаю колонны и заводы
    useEffect(() => {
      if (selectedGroup?.id && !isGroupFromPredefined) {
        if (resetSelect) {
          setValue('factory', null);
          setValue('column', null);
          setValue('subdivision', null);
          setValue('columnBlock', null);
          setValue('demand', null);
          setValue('process', null);
        }

        if (debounceGroupRef.current) {
          clearTimeout(debounceGroupRef.current);
        }

        debounceGroupRef.current = setTimeout(() => {
          dispatch(fetchData('vpo-columns', { group: selectedGroup.id }, { page: 1, pageSize: 100 }));
          dispatch(fetchData('vpo-factories', { group: selectedGroup.id }, { page: 1, pageSize: 100 }));
        }, 300);
      }
    }, [selectedGroup, dispatch, setValue, resetSelect, isGroupFromPredefined]);

    //! При изменении завода получаю подразделение
    useEffect(() => {
      if (selectedFactory?.id && selectedColumn?.id && (!isFactoryFromPredefined || !isColumnFromPredefined)) {
        if (resetSelect && !isFactoryFromPredefined && !isColumnFromPredefined) {
          setValue('subdivision', null);
        }

        if (debounceFactoryColumnRef.current) {
          clearTimeout(debounceFactoryColumnRef.current);
        }

        debounceFactoryColumnRef.current = setTimeout(() => {
          dispatch(
            fetchData(
              'vpo-departments',
              {
                factories: selectedFactory.id,
                columns: selectedColumn.id,
              },
              {
                page: 1,
                pageSize: 100,
              }
            )
          );
        }, 400);
      }
    }, [selectedFactory, selectedColumn, dispatch, setValue, resetSelect, isFactoryFromPredefined, isColumnFromPredefined]);

    //! При изменении колонны получаю блок колонны
    useEffect(() => {
      if (selectedColumn?.id && !isColumnFromPredefined) {
        if (resetSelect) {
          setValue('subdivision', null);
          setValue('columnBlock', null);
          setValue('demand', null);
          setValue('process', null);
        }

        if (debounceColumnRef.current) {
          clearTimeout(debounceColumnRef.current);
        }

        debounceColumnRef.current = setTimeout(() => {
          dispatch(
            fetchData(
              'vpo-column-blocks',
              {
                column: selectedColumn.id,
              },
              {
                page: 1,
                pageSize: 100,
              }
            )
          );
        }, 300);
      }
    }, [selectedColumn, dispatch, setValue, resetSelect, isColumnFromPredefined]);

    //! При изменении блока колонны получаю требования
    useEffect(() => {
      if (selectedColumnBlock?.id && !isColumnBlockFromPredefined) {
        if (resetSelect) {
          setValue('demand', null);
          setValue('process', null);
        }

        if (debounceColumnBlockRef.current) {
          clearTimeout(debounceColumnBlockRef.current);
        }

        debounceColumnBlockRef.current = setTimeout(() => {
          dispatch(
            fetchData(
              'vpo-requirements',
              {
                block: selectedColumnBlock.id,
              },
              {
                page: 1,
                pageSize: 100,
              }
            )
          );
        }, 400);
      }
    }, [selectedColumnBlock, dispatch, setValue, resetSelect, isColumnBlockFromPredefined]);

    const onSubmit = useCallback(
      (data: any) => {
        const requestBody = {
          Inconsistencies: `Inconsistencies #${Math.floor(Math.random() * 1000)}`,
          column: data.column?.id,
          columnBlock: data.columnBlock?.id,
          department: data.subdivision?.id,
          process: data.process?.id,
          requirement: data.demand?.id,
          group: data.group?.id,
          factory: data.factory?.id,
          month: currentMonth,
          year: currentYear,
        };

        // console.log('requestBody', requestBody);

        if (firstActionId || lastAddedId) {
          dispatch(setLastAddedId('vpo-actions', null));
          toggleDoing(false);
          return;
        }

        dispatch(addDataVPO('vpo-actions', requestBody));
      },
      [dispatch, firstActionId, lastAddedId, toggleDoing, currentMonth, currentYear]
    );

    const closeForm = useCallback(() => {
      toggleDoing(false);
      if (onActionAdded) {
        onActionAdded();
      }
      dispatch(setLastAddedId('vpo-actions', null));
    }, [toggleDoing, onActionAdded, dispatch]);

    const cancel = useCallback(() => {
      if (isNewForm && lastAddedIdforDelete) {
        dispatch(deleteDataVPO('vpo-actions', lastAddedIdforDelete));
        dispatch(setLastAddedId('vpo-actions', null));
      }
      toggleDoing(false);
    }, [toggleDoing, isNewForm, lastAddedId, dispatch]);

    const handleRemoveInconsistency = useCallback(
      async (index: number, dbDocumentId?: string) => {
        if (dbDocumentId) {
          await dispatch(deleteDataVPO('vpo-inconsistencies', dbDocumentId));
        }
        remove(index);
      },
      [dispatch, remove]
    );

    const isGroupDisabled = false;
    const isFactoryDisabled = !selectedGroup?.id;
    const isColumnDisabled = !selectedGroup?.id;
    const isDepartmentDisabled = !selectedFactory?.id || !selectedColumn?.id;
    const isColumnBlockDisabled = !selectedColumn?.id;
    const isRequirementDisabled = !selectedColumnBlock?.id;

    const [isAutoFillComplete, setIsAutoFillComplete] = useState(false);

    //! Предзаполнение формы из predefinedValues
    useEffect(() => {
      if (!predefinedValues || isAutoFillComplete) {
        return;
      }
      let fieldsSet = 0;
      const totalFields =
        (predefinedValues.group ? 1 : 0) +
        (predefinedValues.factory ? 1 : 0) +
        (predefinedValues.column ? 1 : 0) +
        (predefinedValues.subdivision ? 1 : 0) +
        (predefinedValues.columnBlock ? 1 : 0) +
        (predefinedValues.demand ? 1 : 0) +
        (predefinedValues.process ? 1 : 0);

      if (predefinedValues.group && !selectedGroup) {
        setValue('group', predefinedValues.group);
        fieldsSet++;
      }
      if (predefinedValues.factory && !selectedFactory) {
        setValue('factory', predefinedValues.factory);
        fieldsSet++;
      }
      if (predefinedValues.column && !selectedColumn) {
        setValue('column', predefinedValues.column);
        fieldsSet++;
      }
      if (predefinedValues.subdivision && !watch('subdivision')) {
        setValue('subdivision', predefinedValues.subdivision);
        fieldsSet++;
      }
      if (predefinedValues.columnBlock && !selectedColumnBlock) {
        setValue('columnBlock', predefinedValues.columnBlock);
        fieldsSet++;
      }
      if (predefinedValues.demand && !selectedDemand) {
        setValue('demand', predefinedValues.demand);
        fieldsSet++;
      }
      if (predefinedValues.process && !selectedProcess) {
        setValue('process', predefinedValues.process);
        fieldsSet++;
      }

      if (fieldsSet === totalFields && totalFields > 0) {
        setIsAutoFillComplete(true);
      }
    }, [predefinedValues, selectedGroup, selectedFactory, selectedColumn, selectedColumnBlock, selectedDemand, selectedProcess, isAutoFillComplete, setValue]);

    const [isInitialSaveComplete, setIsInitialSaveComplete] = useState(false);

    //! Если действие уже существует в базе - не создаем новое
    useEffect(() => {
      if (firstActionId && isNewForm) {
        setIsInitialSaveComplete(true);
      }
    }, [firstActionId, isNewForm]);

    //! Автосохранение при предзаполнении формы
    useEffect(() => {
      if (!isNewForm || !isAutoFillComplete || isInitialSaveComplete || firstActionId) {
        return;
      }
      const timer = setTimeout(() => {
        if (firstActionId) {
          return;
        }

        const currentValues = methods.getValues();
        const allFieldsFilled = currentValues.group && currentValues.factory && currentValues.column && currentValues.subdivision && currentValues.columnBlock && currentValues.demand;
        if (allFieldsFilled) {
          const requestBody = {
            Inconsistencies: `Inconsistencies #${Math.floor(Math.random() * 1000)}`,
            column: currentValues.column?.id,
            columnBlock: currentValues.columnBlock?.id,
            department: currentValues.subdivision?.id,
            process: currentValues.process?.id,
            requirement: currentValues.demand?.id,
            group: currentValues.group?.id,
            factory: currentValues.factory?.id,
            month: currentMonth,
            year: currentYear,
          };

          dispatch(addDataVPO('vpo-actions', requestBody));
          setIsInitialSaveComplete(true);
        }
      }, 1200); 

      return () => clearTimeout(timer);
    }, [isNewForm, isAutoFillComplete, isInitialSaveComplete, firstActionId, dispatch, methods, currentMonth, currentYear]);

    const actionIdForinconsistences = lastAddedId || 0;
    // console.log('lastAddedId',lastAddedId)
    useEffect(() => {
      if (lastAddedId) {
        setTimeout(() => {
          const inconsistencesElement = document.querySelector('[data-inconsistences]');
          if (inconsistencesElement) {
            inconsistencesElement.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          }
        }, 100);
      }
    }, [lastAddedId]);

    useEffect(() => {
      return () => {
        if (debounceGroupRef.current) clearTimeout(debounceGroupRef.current);
        if (debounceFactoryColumnRef.current) clearTimeout(debounceFactoryColumnRef.current);
        if (debounceColumnRef.current) clearTimeout(debounceColumnRef.current);
        if (debounceColumnBlockRef.current) clearTimeout(debounceColumnBlockRef.current);
      };
    }, []);

    useEffect(() => {
      if (!firstActionId || isInconsistenciesLoaded) return;
      if (!loadedInconsistencies || loadedInconsistencies.length === 0) return;

      const timer = setTimeout(() => {
        const filtered = loadedInconsistencies.filter((inc: any) => inc.IdVPOactions === firstActionId);
        if (!filtered || filtered.length === 0) return;

        const allMatchCurrentAction = filtered.every((inc: any) => inc.IdVPOactions === firstActionId);
        if (!allMatchCurrentAction) return;

        const formattedInconsistencies = filtered.map((inc: any) => ({
          inconsistency: inc.Inconsistences,
          dbId: inc.id,
          dbDocumentId: inc.documentId,
        }));

        replace(formattedInconsistencies);
        setIsInconsistenciesLoaded(true);
      }, 800); 

      return () => clearTimeout(timer);
    }, [firstActionId, isInconsistenciesLoaded, loadedInconsistencies, replace]);

    useEffect(() => {
      if (editMode && actionData?.inconsistences && actionData.inconsistences.length > 0 && !isInconsistenciesLoaded) {
        const formattedInconsistencies = actionData.inconsistences.map((inc: any) => ({
          inconsistency: inc.Inconsistences,
          dbId: inc.id,
          dbDocumentId: inc.documentId,
        }));

        replace(formattedInconsistencies);
        setIsInconsistenciesLoaded(true);
      }
    }, [editMode, actionData, isInconsistenciesLoaded, replace]);

      const [isLoadingForm, setIsLoadingForm] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoadingForm(false);
    }, 1600);

    return () => clearTimeout(timer);
  }, []);

    return (
      <FormProvider {...methods}>
        {isLoadingForm && !loader && (
          <div className='flex items-center justify-center min-h-[700px]'>
            <Loading />
          </div>
        )}
         {(!isLoadingForm || loader) && (
        <>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className='space-y-5'>
            <section className='grid grid-cols-2 grid-rows-2 gap-5 mb-5 custom-selects [&_.bg-white_span.block]:max-w-[calc(100%-40px)] [&_.bg-white_span.block]:pr-[10px]'>
              <div>
                <Controller
                  rules={{ required: true }}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Select
                      value={value}
                      onChange={onChange}
                      dataField={{
                        placeholder: 'Выберите группу',
                        type: 'select',
                        require: true,
                        title: 'Группа',
                        width: 'w-full',
                        key: 'group',
                        single: true,
                        disabled: isGroupDisabled || !!predefinedValues?.group,
                        options:
                          GroupVPO?.map((item: any) => ({
                            id: item.id,
                            name: item.title,
                          })) || [],
                      }}
                      errors={{ group: !!error }}
                    />
                  )}
                  name='group'
                  control={control}
                />
              </div>
              <div>
                <Controller
                  rules={{ required: true }}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Select
                      value={value}
                      onChange={onChange}
                      dataField={{
                        placeholder: !selectedGroup ? 'Сначала выберите группу' : 'Выберите завод',
                        type: 'select',
                        require: true,
                        title: 'Завод',
                        width: 'w-full',
                        key: 'factory',
                        single: true,
                        disabled: isFactoryDisabled || !!predefinedValues?.factory,
                        options: factoryOptions,
                      }}
                      errors={{ factory: !!error }}
                    />
                  )}
                  name='factory'
                  control={control}
                />
              </div>
              <div>
                <Controller
                  rules={{ required: true }}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Select
                      value={value}
                      onChange={onChange}
                      dataField={{
                        placeholder: !selectedGroup ? 'Сначала выберите группу' : 'Выберите колонну',
                        type: 'select',
                        require: true,
                        title: 'Колонна',
                        width: 'w-full',
                        key: 'column',
                        single: true,
                        disabled: isColumnDisabled || !!predefinedValues?.column,
                        options:
                          ColumnVPO?.map((item: any) => ({
                            id: item.id,
                            name: item.title,
                          })) || [],
                      }}
                      errors={{ column: !!error }}
                    />
                  )}
                  name='column'
                  control={control}
                />
              </div>

              <div>
                <Controller
                  rules={{ required: true }}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Select
                      value={value}
                      onChange={onChange}
                      dataField={{
                        placeholder: !selectedFactory?.id || !selectedColumn?.id ? 'Сначала выберите завод и колонну' : 'Выберите подразделение',
                        type: 'select',
                        require: true,
                        title: 'Подразделение',
                        width: 'w-full',
                        key: 'subdivision',
                        single: true,
                        disabled: isDepartmentDisabled || !!predefinedValues?.subdivision,
                        options:
                          DepartmentVPO?.map((item: any) => ({
                            id: item.id,
                            name: item.title,
                          })) || [],
                      }}
                      errors={{ subdivision: !!error }}
                    />
                  )}
                  name='subdivision'
                  control={control}
                />
              </div>
              <div>
                <Controller
                  rules={{ required: true }}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Select
                      value={value}
                      onChange={onChange}
                      dataField={{
                        placeholder: !selectedColumn?.id ? 'Сначала выберите колонну' : 'Выберите блок колонны',
                        type: 'select',
                        require: true,
                        title: 'Блок колонны',
                        width: 'w-full',
                        key: 'columnBlock',
                        single: true,
                        disabled: isColumnBlockDisabled || !!predefinedValues?.columnBlock,
                        options:
                          ColumnBlockVPO?.map((item: any) => ({
                            id: item.id,
                            name: item.title,
                          })) || [],
                      }}
                      errors={{ columnBlock: !!error }}
                    />
                  )}
                  name='columnBlock'
                  control={control}
                />
              </div>
              <div>
                <Controller
                  rules={{ required: true }}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Select
                      value={value}
                      onChange={(newValue) => {
                        onChange(newValue);
                        if (newValue?.process) {
                          setValue('process', {
                            id: newValue.process.id,
                            name: newValue.process.title,
                          });
                        }
                      }}
                      dataField={{
                        placeholder: !selectedColumnBlock?.id ? 'Сначала выберите блок колонны' : 'Выберите требование',
                        type: 'select',
                        require: true,
                        title: 'Требование',
                        width: 'w-full',
                        key: 'demand',
                        single: true,
                        disabled: isRequirementDisabled || !!predefinedValues?.demand,
                        options:
                          RequirementVPO?.map((item: any) => ({
                            id: item.id,
                            name: item.title,
                            process: item.process,
                          })) || [],
                      }}
                      errors={{ demand: !!error }}
                    />
                  )}
                  name='demand'
                  control={control}
                />
              </div>
            </section>
            <div>
              <Controller
                rules={{ required: true }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Select
                    value={value}
                    onChange={onChange}
                    dataField={{
                      placeholder: !watch('demand') ? 'Сначала выберите требование' : 'Процесс установлен автоматически',

                      type: 'select',
                      require: true,
                      title: 'Процесс',
                      width: 'w-full',
                      key: 'process',
                      single: true,
                      disabled: true,
                      options: value ? [value] : [],
                    }}
                    errors={{ process: !!error }}
                  />
                )}
                name='process'
                control={control}
              />
            </div>
          </div>
        </form>

        {fields.map((field: any, index) => (
          <div key={field.id} className='flex items-start'>
            <div className='w-full mb-3'>
              <Inconsistences
                isRequired={isInconsistencyRequired}
                index={index}
                value={field.inconsistency}
                inconsistenceId={field.dbId}
                inconsistenceDocumentId={field.dbDocumentId}
                disabled={!!predefinedValues?.columnID}
                firstActionId={firstActionId}
                editMode={editMode}
                inconsistenceData={editMode && actionData?.inconsistences?.[index]}
              />
            </div>
            <TGButton
              type='button'
              icon={<TrashIcon width={20} height={20} />}
              onClick={() => handleRemoveInconsistency(index, field.dbDocumentId)}
              className={`translate-y-[6px] ml-2 ${fields.length > 1 ? '' : 'hidden'}`}
            />
          </div>
        ))}
        <TGButton className='w-full' type='button' icon={<PlusCircleIcon width={20} height={20} />} onClick={() => append({ inconsistency: '' })}>
          Добавить несоответствие
        </TGButton>

        <FlexRow className='justify-between mt-10'>
          <TGButton type='button' onClick={cancel}>
            Отмена
          </TGButton>
          {firstActionId || lastAddedId ? (
            <TGButton type='submit' color='bluedark' onClick={closeForm}>
              Сохранить
            </TGButton>
          ) : (
            <TGButton type='submit' color='bluedark' onClick={handleSubmit(onSubmit)}>
              Сохранить
            </TGButton>
          )}
        </FlexRow>
        </>
        )}
      </FormProvider>
    );
  }
);
